// UI-side English glossary for the ontology IDs carried by the S5 prompt list.
// The glosses themselves live in knowledge/glossary.go; this module reads them
// out of the generated vocabulary artifact (cmd/gennlp) instead of mirroring
// them, so the two cannot drift. Nothing stored depends on it — it exists so the
// voice page can render human text from vocabulary-free IDs. An unknown ID falls
// back to the ID string itself, so a newly added ontology ID is never silently
// dropped while its label is missing. English only during R&D.

import vocabulary from "@/lib/generated/vocabulary.json";
import type { Prompt } from "@/lib/extraction";

/** English gloss per concept ID, read from the generated glossary. */
const conceptLabels: Record<string, string> =
	vocabulary.knowledge.conceptLabels;

/** English gloss per attribute ID, read from the generated glossary. */
const attributeLabels: Record<string, string> =
	vocabulary.knowledge.attributeLabels;

/**
 * English gloss per situation ID. These four strings are hand-authored UI copy
 * rather than generated: the wire carries only the symbolic situation ID and
 * its reason token, so there is no English on it to read
 * (Nidus-iOS/doc/design/layered-extraction.md §4.4). The spoken phrasings for
 * the same four situations *are* generated — `speech.variants` in the artifact,
 * keyed by exactly these IDs.
 */
const situationLabels: Record<string, string> = {
	"access-blockers": "Access blockers?",
	features:
		"Please describe any features that may be creating breeding habitats",
	"property-access": "Property access?",
	"safety-blockers": "Any safety concerns?",
};

/** English gloss for a concept ID, falling back to the ID string. */
export function conceptLabel(id: string): string {
	return conceptLabels[id] ?? id;
}

/** English gloss for an attribute ID, falling back to the ID string. */
export function attributeLabel(id: string): string {
	return attributeLabels[id] ?? id;
}

/**
 * Render a prompt as the text the technician reads: a `situation` prompt's
 * directive — written as the question or instruction itself — otherwise
 * `Treatment · Amount`, or the concept alone when the prompt carries no
 * attribute. An unknown situation ID falls back to the ID string.
 */
export function promptLabel(prompt: Prompt): string {
	if (prompt.situation) {
		return situationLabels[prompt.situation] ?? prompt.situation;
	}
	if (!prompt.attribute) {
		return conceptLabel(prompt.concept);
	}
	return `${conceptLabel(prompt.concept)} · ${attributeLabel(prompt.attribute)}`;
}
